'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { DateValue, now, parseDate, today, toZoned } from '@internationalized/date'
import { Button, Select, SelectItem, SelectSection } from '@nextui-org/react'
import { I18nProvider } from '@react-aria/i18n'
import { format, isAfter, isBefore, parse } from 'date-fns'
import { useLocale, useTranslations } from 'next-intl'
import { union } from 'ramda'
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useEffectOnce } from 'react-use'
import { z } from 'zod'

import { useDestination } from '@/app/[locale]/destination/context/DestinationContext'
import { env } from '@/constants/configs'
import { fetchCommon } from '@/libs/actions/common.action'
import { cookies } from '@/libs/cookie'
import { generateAvailableDatetime } from '@/libs/date'
import { geoBeanchLocation } from '@/libs/haversine'
import { locales } from '@/libs/intl'

import { DatePicker } from '../ui/datepicker'

// Same styles as original booking form
const selectClassNames = {
  trigger: 'shadow-none rounded bg-theme-200/20 hover:!bg-theme-200/25',
  label: 'text-theme/90',
  value: 'font-bold',
  popoverContent: 'rounded'
}
const selectItemClassNames = 'rounded data-[selected=true]:!bg-theme-200/60  '

const formSchema = z.object({
  cs_location: z.string(),
  cs_appdate: z.string(),
  cs_apptime: z.string(),
  cs_guest: z.string(),
  cs_promotion_id: z.string().optional(),
  utm_source: z.string().optional(),
  utm_content: z.string().optional(),
  csrf: z.string().optional()
})

type Form = z.infer<typeof formSchema>

// Interface to define promotion-specific constraints
interface PromotionConstraints {
  branchIds: string
  minGuests?: number
  maxGuests?: number
  advancebooking?: number
  isBuyOneGetOne?: boolean
  expired?: boolean
  expireDate?: string
  availableDateTimes?: Array<{
    start_date: string
    end_date: string
    start_time: string
    end_time: string
  }>
  cs_promotion_id?: number
}

interface PromotionBookingFormProps {
  defaultBranchId?: string
  allowChange?: boolean
  constraints: PromotionConstraints
  source?: string
}

export function PromotionBookingForm({
  defaultBranchId,
  allowChange = true,
  constraints,
  source: sourceProp
}: PromotionBookingFormProps) {
  // __STATE's
  const formRef = useRef<HTMLFormElement>(null)
  const form = useForm<Partial<Form>>({ resolver: zodResolver(formSchema) })

  const locale = useLocale()
  const t = useTranslations()
  const source = cookies.get('source')
  const utmContent = cookies.get('utm_content')

  const currentLocale = useMemo(() => locales.find((r) => r.key === locale), [locale])

  // Branch list is SSR'd by the layout into DestinationProvider — read it instead of refetching.
  const { data: destination } = useDestination()

  const [initialBranchSet, setInitialBranchSet] = useState(false)
  const [userHasSelectedBranch, setUserHasSelectedBranch] = useState(false)
  const [userHasSelectedDate, setUserHasSelectedDate] = useState(false)
  const [branches, setBranches] = useState<IBranch[]>([])
  const [currentBranch, setCurrentBranch] = useState<Branch>()
  const [intervals, setIntervals] = useState<{ hour: number; minute: number }[]>(() =>
    generateAvailableDatetime(now(env.TZ), '10:00', '22:00', env.TZ, false)
  )
  const [currentActionUrl, setCurrentActionUrl] = useState(
    `${env.APP_BASE_URL}/${currentLocale?.key}/onlinebooking/`
  )

  // Parse branch IDs from constraints
  const allowedBranchIds = useMemo(() => {
    return constraints.branchIds?.split(',').filter(Boolean) || []
  }, [constraints.branchIds])

  // __FUNCTION's
  const onSubmit = (data: Partial<Form>) => {
    formRef.current?.submit()
  }
  // Calculate min date with advance booking days
  const current = now(env.TZ)

  const advanceDays = constraints.advancebooking || 0

  const minDate = current.add({ days: advanceDays })

  // Get available dates from constraints if specified
  const availableDates = useMemo(() => {
    if (constraints.availableDateTimes && constraints.availableDateTimes.length > 0) {
      const expandedDates: Array<{
        date: DateValue
        timeRange: { start: string; end: string }
        originalItem: any
      }> = []

      // Convert minDate to date-only for comparison (strip time)
      const minDateOnly =
        today(env.TZ).compare(minDate) <= 0
          ? today(env.TZ)
          : parseDate(
            `${minDate.year}-${String(minDate.month).padStart(2, '0')}-${String(minDate.day).padStart(2, '0')}`
          )

      constraints.availableDateTimes.forEach((item) => {
        try {
          const startDate = parseDate(item.start_date)
          const endDate = parseDate(item.end_date)

          // Expand the date range from start_date to end_date
          let currentDate = startDate
          while (currentDate.compare(endDate) <= 0) {
            const zonedDate = toZoned(currentDate, env.TZ)

            // Compare only date parts, not time
            if (currentDate.compare(minDateOnly) >= 0) {
              expandedDates.push({
                date: zonedDate,
                timeRange: { start: item.start_time, end: item.end_time },
                originalItem: item
              })
            } else {
            }

            // Move to next day
            currentDate = currentDate.add({ days: 1 })
          }
        } catch (error) {
          console.error(
            'Error parsing available date range:',
            item.start_date,
            item.end_date,
            error
          )
        }
      })

      return expandedDates
    }
    return []
  }, [constraints.availableDateTimes, minDate])

  // Function to check if a date is available
  const isDateAvailable = useCallback(
    (date: DateValue) => {
      if (availableDates.length === 0) return true // No restrictions

      const dateToCheck = toZoned(date, env.TZ)
      return availableDates.some((availableDate) => availableDate.date.compare(dateToCheck) === 0)
    },
    [availableDates]
  )
  // Get time range for a specific date
  const getTimeRangeForDate = useCallback(
    (date: DateValue) => {
      if (availableDates.length === 0) return null

      const dateToCheck = toZoned(date, env.TZ)

      // Find all matching dates and merge their time ranges
      const matchingDates = availableDates.filter(
        (availableDate) => availableDate.date.compare(dateToCheck) === 0
      )

      if (matchingDates.length === 0) return null

      // If multiple time ranges exist for the same date, merge them
      if (matchingDates.length === 1) {
        return matchingDates[0].timeRange
      }

      // Merge multiple time ranges - find the earliest start and latest end
      const timeRanges = matchingDates.map((d) => d.timeRange)
      const earliestStart = timeRanges.reduce(
        (earliest, current) => (current.start < earliest ? current.start : earliest),
        timeRanges[0].start
      )
      const latestEnd = timeRanges.reduce(
        (latest, current) => (current.end > latest ? current.end : latest),
        timeRanges[0].end
      )

      return { start: earliestStart, end: latestEnd }
    },
    [availableDates]
  )
  // Max date: expiration date or 1 year from today
  const maxDate = useMemo(() => {
    // If we have specific available dates, use the latest one
    if (availableDates.length > 0) {
      return availableDates.reduce(
        (latest, current) => (current.date.compare(latest) > 0 ? current.date : latest),
        availableDates[0].date
      )
    }

    // If we have availableDateTimes but no expanded dates (all filtered out),
    // still calculate from the original end dates
    if (constraints.availableDateTimes && constraints.availableDateTimes.length > 0) {
      try {
        const latestEndDate = constraints.availableDateTimes.reduce((latest, current) => {
          const currentEndDate = parseDate(current.end_date)
          const latestEndDate = parseDate(latest.end_date)
          return currentEndDate.compare(latestEndDate) > 0 ? current : latest
        })
        return toZoned(parseDate(latestEndDate.end_date), env.TZ)
      } catch (error) {
        console.error('Error calculating max date from availableDateTimes:', error)
      }
    }

    // Fallback to original logic
    if (constraints.expireDate) {
      try {
        // Parse YYYY-MM-DD date format
        const [year, month, day] = constraints.expireDate.split('-').map(Number)
        if (year && month && day) {
          return toZoned(
            parseDate(
              `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
            ),
            env.TZ
          )
        }
      } catch (error) {
        console.error('Error parsing expiration date:', error)
      }
    }
    return today(env.TZ).add({ years: 1 })
  }, [constraints.expireDate, constraints.availableDateTimes, availableDates])

  // Check if the current date is past the expiration date
  const isExpired = useMemo(() => {
    if (constraints.expireDate) {
      try {
        const expirationDate = parse(constraints.expireDate, 'yyyy-MM-dd', new Date())
        return isAfter(new Date(), expirationDate)
      } catch (error) {
        console.error('Error checking expiration:', error)
        return false
      }
    }
    return constraints.expired || false
  }, [constraints.expireDate, constraints.expired])

  // Filter branches based on allowed branch IDs
  const filterBranches = useCallback(
    (branchList: IBranch[]) => {
      if (allowedBranchIds.length === 0) return branchList

      return branchList
        .map((cityGroup) => ({
          ...cityGroup,
          branches: cityGroup.branches.filter((branch) => allowedBranchIds.includes(branch.id))
        }))
        .filter((cityGroup) => cityGroup.branches.length > 0)
    },
    [allowedBranchIds]
  )

  // Generate guest options based on constraints
  const getGuestOptions = useCallback(() => {
    const min = constraints.minGuests || 1
    const max = constraints.maxGuests || 10

    let guestNumbers = Array.from({ length: max - min + 1 }).map((_, i) => min + i)

    // For buy-one-get-one promotions, only show even numbers
    if (constraints.isBuyOneGetOne) {
      guestNumbers = guestNumbers.filter((num) => num % 2 === 0)

      // If min is odd, start from the next even number
      if (min % 2 !== 0 && guestNumbers.length === 0) {
        guestNumbers = [min + 1]
      }
    }

    return guestNumbers
  }, [constraints])

  const handleBranchChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const branchId = e.target.value
      const branch = branches
        ?.map((r) => r.branches)
        .reduce<Branch[]>(union, [])
        .find((r) => r.id === branchId)

      if (branch) {
        setCurrentBranch(branch)
        setUserHasSelectedBranch(true) // Mark that user has made a selection

        let newIntervals: { hour: number; minute: number }[] = []
        // If user hasn't selected a date yet, show default hours without +2h rule
        if (!userHasSelectedDate) {
          newIntervals = generateAvailableDatetime(now(env.TZ), '10:00', '22:00', env.TZ, false)
        } else {
          // User has selected date, use branch hours with appropriate rules
          const selectedDate = form.watch('cs_appdate')
          if (selectedDate) {
            // Parse the selected date and determine if 2h rule should apply
            const appdate = toZoned(
              parseDate(
                `${selectedDate.slice(0, 4)}-${selectedDate.slice(4, 6)}-${selectedDate.slice(6, 8)}`
              ),
              env.TZ
            )
            const isToday = appdate.toDate().toDateString() === now(env.TZ).toDate().toDateString()

            // Check if this date has specific promotion time constraints
            const specificTimeRange = getTimeRangeForDate(appdate)

            if (specificTimeRange) {
              // Use promotion-specific time range
              newIntervals = generateAvailableDatetime(
                appdate,
                specificTimeRange.start,
                specificTimeRange.end,
                env.TZ,
                isToday
              )
            } else {
              // Use new branch operating hours for the existing date
              const [open, close] = [branch.open_timename, branch.close_timename]
              newIntervals = generateAvailableDatetime(appdate, open, close, env.TZ, isToday)
            }
          } else {
            const [open, close] = [branch.open_timename, branch.close_timename]
            newIntervals = generateAvailableDatetime(now(env.TZ), open, close, env.TZ, false)
          }
        }
        setIntervals(newIntervals)

        // Check if the current selected time is still available
        const selectedTime = form.watch('cs_apptime')
        const availableTimes = newIntervals.map(
          (record) =>
            `${String(record.hour).padStart(2, '0')}:${String(record.minute).padStart(2, '0')}`
        )
        if (!selectedTime || !availableTimes.includes(selectedTime)) {
          form.resetField('cs_apptime')
        }

        if (branch?.cross_http_origin) {
          setCurrentActionUrl(`${branch.cross_http_origin}/${currentLocale?.key}/onlinebooking/`)
        } else {
          setCurrentActionUrl(`${env.APP_BASE_URL}/${currentLocale?.key}/onlinebooking/`)
        }

        form.setValue('cs_location', branchId)
      }
    },
    [branches, currentLocale, form, userHasSelectedDate, getTimeRangeForDate]
  )
  const handleDateChange = useCallback(
    (value: DateValue | undefined) => {
      let open = '10:00'
      let close = '22:00'

      if (currentBranch) {
        ;[open, close] = [currentBranch.open_timename, currentBranch.close_timename]
      }

      // The picker's reset button passes `undefined`: clear both fields and drop back to the
      // pre-selection slot list so the time Select is not left holding a stale date's intervals.
      if (!value) {
        setUserHasSelectedDate(false)
        form.resetField('cs_appdate')
        form.resetField('cs_apptime')
        setIntervals(generateAvailableDatetime(now(env.TZ), open, close, env.TZ, false))
        return
      }

      const appdate = toZoned(value, env.TZ)
      setUserHasSelectedDate(true)

      const isToday = appdate.toDate().toDateString() === now(env.TZ).toDate().toDateString()
      const specificTimeRange = getTimeRangeForDate(value)

      const newIntervals = specificTimeRange
        ? generateAvailableDatetime(appdate, specificTimeRange.start, specificTimeRange.end, env.TZ, isToday)
        : generateAvailableDatetime(appdate, open, close, env.TZ, isToday)

      setIntervals(newIntervals)

      const selectedTime = form.watch('cs_apptime')
      const availableTimes = newIntervals.map(
        (record) =>
          `${String(record.hour).padStart(2, '0')}:${String(record.minute).padStart(2, '0')}`
      )
      if (!selectedTime || !availableTimes.includes(selectedTime)) {
        form.resetField('cs_apptime')
      }
      form.setValue('cs_appdate', format(appdate.toDate(), 'yyyyMMdd'))
    },
    [currentBranch, form, getTimeRangeForDate]
  )

  const initializeBranch = useCallback(
    (branchList: IBranch[]) => {
      if (defaultBranchId && !initialBranchSet) {
        const allBranches = branchList.map((r) => r.branches).reduce<Branch[]>(union, [])
        const branch = allBranches.find((b) => b.id === defaultBranchId)

        if (branch) {
          setCurrentBranch(branch)
          setUserHasSelectedBranch(true) // Mark as selected since it's explicitly set
          form.setValue('cs_location', branch.id)

          // Initialize with default hours without 2h rule since no date selected yet
          const intervals = generateAvailableDatetime(now(env.TZ), '10:00', '22:00', env.TZ, false)
          setIntervals(intervals)

          if (branch?.cross_http_origin) {
            setCurrentActionUrl(`${branch.cross_http_origin}/${currentLocale?.key}/onlinebooking/`)
          }
          setInitialBranchSet(true)
        }
      }
    },
    [defaultBranchId, currentLocale, form, initialBranchSet]
  )
  // __EFFECT's
  useEffectOnce(() => {
    // Branch list is already in hand from the layout's SSR — apply it before anything awaits, so
    // the select is populated on first paint instead of after a round trip.
    const branches: IBranch[] | undefined = destination?.branches
    const filteredBranches = branches?.length ? filterBranches(branches) : []

    if (filteredBranches.length) {
      setBranches(filteredBranches)
      initializeBranch(filteredBranches)
    }

    async function fetchCsrf() {
      // Still fetched: the CSRF token must be fresh at submit time, a cached page's is not.
      const common = await fetchCommon(locale)
      if (common) form.setValue('csrf', common.CSRF_TOKEN)
    }

    function selectNearest() {
      if (!filteredBranches.length) return

      // Auto-select nearest branch only if no default branch AND user hasn't selected one
      if (!defaultBranchId && !userHasSelectedBranch) {
        // Start geolocation immediately to reduce delay
        const allBranches = filteredBranches.map((r) => r.branches).reduce<Branch[]>(union, [])

        // Use Promise to handle geolocation asynchronously
        geoBeanchLocation(allBranches)
          .then((nearestBranch) => {
            // Double-check user hasn't selected a branch while geolocation was running
            if (nearestBranch && !userHasSelectedBranch) {
              // Sort branches to show nearest city first. Copy first — with no allow-list
              // `filterBranches` hands back the context's own array, and sorting mutates it.
              const branchSorted = [...filteredBranches].sort((a) =>
                a.code === nearestBranch.city_code ? -1 : 1
              )
              setBranches(branchSorted)

              // Only auto-select if user still hasn't made a selection
              if (!userHasSelectedBranch) {
                setCurrentBranch(nearestBranch)
                form.setValue('cs_location', nearestBranch.id)

                // Use default hours without 2h rule since no date selected yet
                const intervals = generateAvailableDatetime(
                  now(env.TZ),
                  '10:00',
                  '22:00',
                  env.TZ,
                  false
                )
                setIntervals(intervals)

                if (nearestBranch?.cross_http_origin) {
                  setCurrentActionUrl(
                    `${nearestBranch.cross_http_origin}/${currentLocale?.key}/onlinebooking/`
                  )
                }
              }
            }
          })
          .catch((error) => {
            // Keep original branches order if geolocation fails
          })
      }
    }

    fetchCsrf()
    selectNearest()
    form.setValue('utm_source', source)
    form.setValue('utm_content', utmContent)

    // Set promotion ID if provided
    if (constraints.cs_promotion_id) {
      form.setValue('cs_promotion_id', constraints.cs_promotion_id.toString())
    }

    // Set default guest count to minimum if specified
    if (constraints.minGuests) {
      const defaultGuests =
        constraints.isBuyOneGetOne && constraints.minGuests % 2 !== 0
          ? constraints.minGuests + 1 // Ensure even number for buy-one-get-one
          : constraints.minGuests
      form.setValue('cs_guest', String(defaultGuests))
    }
  })

  // __RENDER
  return (
    <form
      className='flex min-w-72 flex-col gap-8 border-b-2 border-gray-300 pb-4 md:flex-row md:gap-2 md:py-4'
      method='post'
      action={currentActionUrl}
      onSubmit={form.handleSubmit(onSubmit)}
      ref={formRef}>
      <input type='hidden' {...form.register('csrf')} />
      <input type='hidden' {...form.register('cs_location')} />
      <input type='hidden' {...form.register('cs_appdate')} />
      <input type='hidden' {...form.register('cs_apptime')} />
      <input type='hidden' {...form.register('cs_guest')} />
      <input type='hidden' {...form.register('cs_promotion_id')} />
      <input type='hidden' {...form.register('utm_source')} />
      <input type='hidden' {...form.register('utm_content')} />
      <Select
        classNames={selectClassNames}
        key='PLACEHOLDER_LOCATION'
        variant='flat'
        size='sm'
        radius='none'
        label={t('Menu.PLACEHOLDER_LOCATION')}
        labelPlacement='inside'
        isRequired
        isDisabled={!allowChange && defaultBranchId !== undefined}
        scrollShadowProps={{ isEnabled: true }}
        errorMessage={form.formState.errors?.cs_location?.message}
        onChange={handleBranchChange}
        selectedKeys={(() => {
          const selectedBranch = form.watch('cs_location')
          if (selectedBranch) return [selectedBranch]
          if (defaultBranchId && !initialBranchSet) return [defaultBranchId]
          return []
        })()}
        defaultSelectedKeys={defaultBranchId ? [defaultBranchId] : []}>
        {branches?.map((record) => (
          <SelectSection
            classNames={{ heading: 'text-sm font-bold text-theme' }}
            title={record.name}
            key={record.code}>
            {record.branches.map((branch) => (
              <SelectItem className={selectItemClassNames} value={branch.id} key={branch.id}>
                {branch.name}
              </SelectItem>
            ))}
          </SelectSection>
        ))}
      </Select>{' '}
      {/* Date Selection */}
      <I18nProvider locale={currentLocale?.locale}>
        <DatePicker
          lang={currentLocale?.locale}
          key='PLACEHOLDER_DATE'
          label={t('Menu.PLACEHOLDER_DATE')}
          value={form.watch('cs_appdate')}
          minDate={minDate}
          maxDate={maxDate}
          isDateUnavailable={
            constraints.availableDateTimes ? (date) => !isDateAvailable(date) : undefined
          }
          errorMessage={
            form.formState.errors?.cs_appdate?.message ||
            (isExpired ? 'Promotion has expired' : undefined)
          }
          onChange={handleDateChange}
        />
      </I18nProvider>
      <Select
        classNames={selectClassNames}
        key={`PLACEHOLDER_TIME_${currentBranch?.id || 'default'}_${form.watch('cs_appdate') || 'today'}_${intervals.length}`}
        variant='flat'
        size='sm'
        radius='none'
        label={t('Menu.PLACEHOLDER_TIME')}
        labelPlacement='inside'
        selectedKeys={(() => {
          const selectedTime = form.watch('cs_apptime')
          return selectedTime ? [selectedTime] : []
        })()}
        isRequired
        scrollShadowProps={{ isEnabled: false }}
        errorMessage={form.formState.errors?.cs_apptime?.message}
        onChange={({ target }) => form.setValue('cs_apptime', target.value)}>
        {intervals.map((record) => {
          const hours = String(record.hour).padStart(2, '0')
          const minutes = String(record.minute).padStart(2, '0')
          const value = `${hours}:${minutes}`

          return (
            <SelectItem className={selectItemClassNames} value={value} key={value}>
              {value}
            </SelectItem>
          )
        })}
      </Select>
      <Select
        classNames={selectClassNames}
        key='PLACEHOLDER_GUEST_BG'
        variant='flat'
        size='sm'
        radius='none'
        label={t('Menu.PLACEHOLDER_GUEST_BG')}
        labelPlacement='inside'
        isRequired
        scrollShadowProps={{ isEnabled: false }}
        errorMessage={form.formState.errors?.cs_guest?.message}
        onChange={({ target }) => form.setValue('cs_guest', target.value)}>
        {getGuestOptions().map((guestNum) => (
          <SelectItem className={selectItemClassNames} value={guestNum} key={guestNum}>
            {String(guestNum)}
          </SelectItem>
        ))}
      </Select>
      <Button
        className='rounded bg-theme-orange py-4 text-sm font-semibold text-white shadow-sm'
        type='submit'
        size='lg'
        radius='none'
        id={sourceProp ? `booking-submit-${sourceProp}` : 'booking-submit-default'}
        data-track='booking-submit-form'
        aria-label='Button submit booking'>
        {t('Menu.BOOKNOW_BUTTON')}
      </Button>
    </form>
  )
}
